import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

type Status = "loading" | "success" | "failed";

export default function PaymentCallback() {
    const { accessToken } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [status, setStatus] = useState<Status>("loading");
    const [amount, setAmount] = useState<number | null>(null);
    const [refId, setRefId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const verify = async () => {
            const authority = searchParams.get("Authority");
            const status = searchParams.get("Status");

            if (!authority || status !== "OK") {
                setStatus("failed");
                setErrorMessage("پرداخت توسط کاربر لغو شد یا با خطا مواجه شد.");
                return;
            }

            try {
                const res = await api.post(
                    "/wallet/verify",
                    { authority, status },
                    { headers: { Authorization: accessToken ? `Bearer ${accessToken}` : "" } }
                );

                if (res.data?.success) {
                    setStatus("success");
                    setAmount(res.data.amount ?? null);
                    setRefId(res.data.ref_id ?? null);
                } else {
                    setStatus("failed");
                    setErrorMessage(res.data?.message || "تراکنش تأیید نشد.");
                }
            } catch (err: any) {
                setStatus("failed");
                setErrorMessage(
                    err?.response?.data?.message || "خطا در تأیید پرداخت. لطفاً با پشتیبانی تماس بگیرید."
                );
            }
        };

        verify();
    }, []);

    return (
        <div dir="rtl" className="min-h-screen bg-darkbg flex items-center justify-center px-5">
            <div className="max-w-[380px] w-full text-center animate-fadeIn">

                {status === "loading" && (
                    <div className="flex flex-col items-center gap-6">
                        <div className="relative w-20 h-20">
                            <svg className="animate-spin w-20 h-20" viewBox="0 0 80 80" fill="none">
                                <circle cx="40" cy="40" r="36" stroke="rgba(244,180,0,0.15)" strokeWidth="4" />
                                <path d="M40 4a36 36 0 0 1 36 36" stroke="#f4b400" strokeWidth="4" strokeLinecap="round" />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#f4b400" strokeWidth="1.5" />
                                </svg>
                            </div>
                        </div>
                        <div>
                            <p className="text-white text-lg font-bold mb-1">در حال تأیید پرداخت</p>
                            <p className="text-white/40 text-sm">لطفاً صفحه را نبندید...</p>
                        </div>
                    </div>
                )}

                {status === "success" && (
                    <div className="flex flex-col items-center gap-5">
                        <div className="relative w-24 h-24">
                            <svg className="w-24 h-24" viewBox="0 0 96 96" fill="none">
                                <circle cx="48" cy="48" r="44" stroke="rgba(0,230,118,0.2)" strokeWidth="3" />
                                <circle
                                    cx="48" cy="48" r="44"
                                    stroke="#00e676"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeDasharray="276"
                                    strokeDashoffset="0"
                                    style={{ animation: "dash 0.6s ease forwards" }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" style={{ animation: "popIn 0.3s 0.5s ease both" }}>
                                    <path d="M5 13l4 4L19 7" stroke="#00e676" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        <div>
                            <p className="text-[#00e676] text-xl font-bold mb-1">پرداخت موفق!</p>
                            <p className="text-white/50 text-sm">کیف پول شما با موفقیت شارژ شد.</p>
                        </div>

                        <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-right space-y-3">
                            {amount !== null && (
                                <div className="flex justify-between items-center">
                                    <span className="text-white/50 text-sm">مبلغ شارژ</span>
                                    <span className="text-white font-bold">
                                        {Number(amount).toLocaleString("fa-IR")}
                                        <span className="text-white/40 text-xs mr-1">تومان</span>
                                    </span>
                                </div>
                            )}
                            {refId && (
                                <div className="flex justify-between items-center">
                                    <span className="text-white/50 text-sm">کد پیگیری</span>
                                    <span className="text-primary font-mono text-sm">{refId}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center">
                                <span className="text-white/50 text-sm">وضعیت</span>
                                <span className="text-[#00e676] text-sm">✓ تأیید شده</span>
                            </div>
                        </div>

                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => navigate("/profile")}
                                className="flex-1 py-3 bg-primary text-black font-bold rounded-xl active:scale-95 transition-all"
                            >
                                بازگشت به پروفایل
                            </button>
                            <button
                                onClick={() => navigate("/")}
                                className="flex-1 py-3 bg-white/10 text-white rounded-xl active:scale-95 transition-all"
                            >
                                صفحه اصلی
                            </button>
                        </div>
                    </div>
                )}

                {status === "failed" && (
                    <div className="flex flex-col items-center gap-5">
                        <div className="relative w-24 h-24">
                            <svg className="w-24 h-24" viewBox="0 0 96 96" fill="none">
                                <circle cx="48" cy="48" r="44" stroke="rgba(255,82,82,0.2)" strokeWidth="3" />
                                <circle cx="48" cy="48" r="44" stroke="#ff5252" strokeWidth="3" strokeLinecap="round" />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6l12 12" stroke="#ff5252" strokeWidth="2.5" strokeLinecap="round" />
                                </svg>
                            </div>
                        </div>

                        <div>
                            <p className="text-red-400 text-xl font-bold mb-1">پرداخت ناموفق</p>
                            <p className="text-white/50 text-sm">{errorMessage}</p>
                        </div>

                        <div className="w-full bg-red-500/5 border border-red-500/20 rounded-2xl p-4 text-sm text-white/50 text-right">
                            در صورتی که مبلغی از حساب شما کسر شده و موجودی کیف پول شارژ نشده، لطفاً با پشتیبانی تماس بگیرید.
                        </div>

                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => navigate("/wallet/charge")}
                                className="flex-1 py-3 bg-primary text-black font-bold rounded-xl active:scale-95 transition-all"
                            >
                                تلاش مجدد
                            </button>
                            <button
                                onClick={() => navigate("/profile")}
                                className="flex-1 py-3 bg-white/10 text-white rounded-xl active:scale-95 transition-all"
                            >
                                بازگشت
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes dash {
                    from { stroke-dashoffset: 276; }
                    to { stroke-dashoffset: 0; }
                }
                @keyframes popIn {
                    from { transform: scale(0); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );
}